import cloneDeep from "lodash.clonedeep";
import { getImageBaseUrl } from "../../apis/apiSettings";

import { baseComparisonItem } from "../baseObjects/baseComparisonItem";

/**
 * A utility function that verifies that that tagsArray is a real array. If not, it returns an empty array.
 *
 * @param {Array} tagsArray - The array containing tag strings.
 *
 * @returns {Array} - An array of tags if tagsArray is and array, otherwise an empty array.
 *
 * @example
 *
 * const validTags = sanitizeTags(["tag1", "tag2", "tag3"]);
 *
 * @throws Will not throw an error but will return an empty array if the tagsArray is not a real array.
 */
export const sanitizeTags = (tagsArray) => {
  try {
    if (Array.isArray(tagsArray)) {
      return tagsArray;
    }
  } catch (e) {
    // Handle parse error, if any
  }

  // return empty list if tagsArray is not an array
  return [];
};

/**
 * A utility function that sanitizes image details.
 * This function is used to parse a JSON string that contains image details and provides default values for any missing properties to ensure that the resulting image details object is valid.
 *
 * @param {string} imageDetailsStr - The JSON string containing image details.
 *
 * @returns {Object} - An image details object with all necessary properties and default values for any missing properties.
 *
 * The returned object includes the following properties:
 * name, size, and caption.
 *
 * Each property is either taken from the parsed JSON or given a default value if it does not exist in the parsed JSON.
 *
 * @example
 *
 * const validImageDetails = sanitizeImageDetails('{"name": "example.png", "size": 1024}');
 *
 * @throws Will not throw an error but will return an object with default values if the JSON string cannot be parsed.
 */
export const sanitizeImageDetails = (imageDetailsStr) => {
  let imageDetails = {};
  try {
    imageDetails = JSON.parse(imageDetailsStr);
  } catch (e) {
    // Handle parse error, if any
  }

  return {
    name: imageDetails.name || "",
    size: imageDetails.size || 0,
    caption: imageDetails.caption || "",
  };
};

/**
 * A utility function that sanitizes pair configuration data.
 * This function is used to parse a JSON string that contains pair configuration details and provides default values for any missing properties to ensure that the resulting pair configuration object is valid.
 *
 * @param {string} pairConfigurationStr - The JSON string containing pair configuration details.
 *
 * @returns {Object} - A pair configuration object with all necessary properties and default values for any missing properties.
 *
 * The returned object includes properties related to image zoom split, position split, lock toggle split, image zoom opacity, position opacity, lock toggle opacity, image one/two position split, image one/two zoom split, image one/two position opacity, image one/two width, image one/two rotation, image one/two opacity, and image one/two zoom opacity.
 *
 * Each property is either taken from the parsed JSON or given a default value if it does not exist in the parsed JSON.
 *
 * @example
 *
 * const validPairConfiguration = sanitizePairConfiguration('{"imageZoomSplit": 50, "positionSplit": { "x": 0, "y": 0 }}');
 *
 * @throws Will not throw an error but will return an object with default values if the JSON string cannot be parsed.
 */
export const sanitizePairConfiguration = (pairConfigurationStr) => {
  let pairConfiguration = {};

  try {
    pairConfiguration = JSON.parse(pairConfigurationStr);
  } catch (e) {
    // Handle parse error, if any
  }

  return {
    imageZoomSplit: pairConfiguration.imageZoomSplit || 100,
    positionSplit: pairConfiguration.positionSplit || { x: 0, y: 0 },
    lockToggleSplit: pairConfiguration.lockToggleSplit || true,
    imageZoomOpacity: pairConfiguration.imageZoomOpacity || 100,
    positionOpacity: pairConfiguration.positionOpacity || { x: 0, y: 0 },
    lockToggleOpacity: pairConfiguration.lockToggleOpacity || true,
    imageOnePositionSplit: pairConfiguration.imageOnePositionSplit || {
      x: 0,
      y: 0,
    },
    imageTwoPositionSplit: pairConfiguration.imageTwoPositionSplit || {
      x: 0,
      y: 0,
    },
    imageOneZoomSplit: pairConfiguration.imageOneZoomSplit || 1,
    imageTwoZoomSplit: pairConfiguration.imageTwoZoomSplit || 1,
    imageOnePositionOpacity: pairConfiguration.imageOnePositionOpacity || {
      x: 0,
      y: 0,
    },
    imageTwoPositionOpacity: pairConfiguration.imageTwoPositionOpacity || {
      x: 0,
      y: 0,
    },
    imageOneWidth: pairConfiguration.imageOneWidth || 100,
    imageTwoWidth: pairConfiguration.imageTwoWidth || 100,
    imageOneRotation: pairConfiguration.imageOneRotation || 0,
    imageTwoRotation: pairConfiguration.imageTwoRotation || 0,
    imageOneOpacity: pairConfiguration.imageOneOpacity || 100,
    imageTwoOpacity: pairConfiguration.imageTwoOpacity || 75,
    imageOneZoomOpacity: pairConfiguration.imageOneZoomOpacity || 1,
    imageTwoZoomOpacity: pairConfiguration.imageTwoZoomOpacity || 1,
  };
};

/**
 * A utility function to get a valid pair configuration object from a comparison item.
 * This function is used to extract all relevant properties from a comparison item object and provide
 * default values for any missing properties to ensure that the resulting pair configuration object is valid.
 *
 * @param {Object} options - The options object.
 * @param {Object} options.comparisonItem - The comparison item object from which to extract the properties.
 *
 * @returns {Object} - A pair configuration object with all necessary properties and default values for any missing properties.
 *
 * The returned object includes the following properties:
 * imageZoomSplit, positionSplit, lockToggleSplit, imageZoomOpacity, positionOpacity, lockToggleOpacity,
 * imageOnePositionSplit, imageTwoPositionSplit, imageOneZoomSplit, imageTwoZoomSplit,
 * imageOnePositionOpacity, imageTwoPositionOpacity, imageOneWidth, imageTwoWidth,
 * imageOneRotation, imageTwoRotation, imageOneOpacity, imageTwoOpacity, imageOneZoomOpacity, imageTwoZoomOpacity.
 *
 * Each property is either taken from the comparisonItem or given a default value if it does not exist in the comparisonItem.
 *
 * @example
 *
 * const validPairConfiguration = getValidPairConfiguration({
 *   comparisonItem: {
 *     imageZoomSplit: 50,
 *     positionSplit: { x: 10, y: 20 },
 *     // ... other properties ...
 *   },
 * });
 */
export const getValidPairConfiguration = ({ comparisonItem }) => {
  const {
    imageZoomSplit,
    positionSplit,
    lockToggleSplit,
    imageZoomOpacity,
    positionOpacity,
    lockToggleOpacity,
    imageOnePositionSplit,
    imageTwoPositionSplit,
    imageOneZoomSplit,
    imageTwoZoomSplit,
    imageOnePositionOpacity,
    imageTwoPositionOpacity,
    imageOneWidth,
    imageTwoWidth,
    imageOneRotation,
    imageTwoRotation,
    imageOneOpacity,
    imageTwoOpacity,
    imageOneZoomOpacity,
    imageTwoZoomOpacity,
  } = comparisonItem;
  return {
    imageZoomSplit: imageZoomSplit || 100,
    positionSplit: positionSplit || { x: 0, y: 0 },
    lockToggleSplit: lockToggleSplit !== undefined ? lockToggleSplit : true,
    imageZoomOpacity: imageZoomOpacity || 100,
    positionOpacity: positionOpacity || { x: 0, y: 0 },
    lockToggleOpacity:
      lockToggleOpacity !== undefined ? lockToggleOpacity : true,
    imageOnePositionSplit: imageOnePositionSplit || { x: 0, y: 0 },
    imageTwoPositionSplit: imageTwoPositionSplit || { x: 0, y: 0 },
    imageOneZoomSplit: imageOneZoomSplit || 1,
    imageTwoZoomSplit: imageTwoZoomSplit || 1,
    imageOnePositionOpacity: imageOnePositionOpacity || { x: 0, y: 0 },
    imageTwoPositionOpacity: imageTwoPositionOpacity || { x: 0, y: 0 },
    imageOneWidth: imageOneWidth || 100,
    imageTwoWidth: imageTwoWidth || 100,
    imageOneRotation: imageOneRotation || 0,
    imageTwoRotation: imageTwoRotation || 0,
    imageOneOpacity: imageOneOpacity || 100,
    imageTwoOpacity: imageTwoOpacity !== undefined ? imageTwoOpacity : 75,
    imageOneZoomOpacity: imageOneZoomOpacity || 1,
    imageTwoZoomOpacity: imageTwoZoomOpacity || 1,
  };
};

/**
 * A utility function to append images and their corresponding details to a FormData object.
 * The function accepts an options object with the following properties:
 *
 * @param {Object} options - The options object.
 * @param {FormData} options.formData - The FormData object to which the images and details will be appended.
 * @param {Array} options.fileInput - An array of File objects. The function uses the first item in this array.
 * @param {Object} options.fileDetails - An object containing details about the file. Currently, only a 'caption' property is used.
 * @param {string} options.fileName - The field name for the file that will be used when appending to the FormData object.
 * @param {string} options.detailsName - The field name for the details that will be used when appending to the FormData object.
 * @param {boolean} options.appendImage - Determines if the image should be appended to the FormData object. Default is true.
 * @param {boolean} options.appendDetails - Determines if the image details should be appended to the FormData object. Default is true.
 *
 * The function handles three cases:
 * 1. If the file is from a previous entry (file.previousEntryItem is true), it extracts the name and size from file.file and appends them along with the caption from fileDetails to the FormData object.
 * 2. If the file is from an API (file.apiItem is true), it extracts the name and size directly from the file and appends them along with the caption from fileDetails to the FormData object.
 * 3. If the file is a local file, it extracts the path (as name) and size from the file and appends them along with the caption from fileDetails to the FormData object.
 *
 * In all cases, it checks the appendImage and appendDetails flags to determine if the image and/or the details should be appended to the FormData object.
 */
export const appendImagesToFormDataCustomField = ({
  formData,
  fileInput,
  fileDetails,
  fileName,
  detailsName,
  appendImage = true,
  appendDetails = true,
  singleFileArray = true,
}) => {
  // file is stored at index 0 in a single file array. When array has many file, they are directly at that index
  const file = singleFileArray ? fileInput[0] : fileInput;

  // console.log(file);

  if (file !== undefined) {
    // Check if the image was from a previous entry (file.previousEntryItem is true)
    if (file.previousEntryItem) {
      const imageDetails = {
        name: file.file.name,
        size: file.file.size,
        caption: fileDetails.caption || "",
      };
      // Append image and image details to the formData
      appendDetails &&
        formData.append(`${detailsName}`, JSON.stringify(imageDetails));
      appendImage && formData.append(`${fileName}`, file.file);
    }
    // Check if the image was loaded from the API (file.apiItem is true)
    else if (file.apiItem) {
      const imageDetails = {
        name: file.name,
        size: file.size,
        caption: fileDetails.caption || "",
      };
      // Append image and image details to the formData
      appendDetails &&
        formData.append(`${detailsName}`, JSON.stringify(imageDetails));
      appendImage && formData.append(`${fileName}`, JSON.stringify(file));
    }
    // If the image was loaded from local client files
    else {
      const imageDetails = {
        name: file.path,
        size: file.size,
        caption: fileDetails.caption || "",
      };
      // Append image and image details to the formData
      appendDetails &&
        formData.append(`${detailsName}`, JSON.stringify(imageDetails));
      appendImage && formData.append(`${fileName}`, file);
    }
  }
};

/**
 * A utility function to sanitize the response from a Celery task.
 * The function takes an object with a single property, celeryProgressResponse, which is the response from a Celery task.
 * It returns an object with properties representing the status and progress of the task.
 *
 * @param {Object} options - The options object.
 * @param {Object} options.celeryProgressResponse - The response from a Celery task.
 *
 * @returns {Object} - An object representing the status and progress of the task:
 *  - calculationComplete: A boolean indicating whether the task is complete. Defaults to false if not provided in the response.
 *  - calculationSuccessful: A boolean indicating whether the task was successful. Defaults to false if not provided in the response.
 *  - calculationPending: A boolean indicating whether the task is pending. Defaults to true if not provided in the response.
 *  - currentStep: A number representing the current step in the task. Defaults to 0 if not provided in the response.
 *  - totalSteps: A number representing the total number of steps in the task. Defaults to 0 if not provided in the response.
 *  - percentComplete: A number representing the percentage of the task that is complete. Defaults to 0 if not provided in the response.
 *  - stepDescription: A string describing the current step. Defaults to an empty string if not provided in the response.
 *  - state: A string representing the state of the task. Defaults to an empty string if not provided in the response.
 */
export const sanitizeCeleryResponse = ({ celeryProgressResponse }) => {
  return {
    calculationComplete:
      celeryProgressResponse.complete !== undefined
        ? celeryProgressResponse.complete
        : false,
    calculationSuccessful:
      celeryProgressResponse.success !== undefined
        ? celeryProgressResponse.success
        : false,
    calculationPending:
      celeryProgressResponse.progress &&
      celeryProgressResponse.progress.pending !== undefined
        ? celeryProgressResponse.progress.pending
        : true,
    currentStep:
      celeryProgressResponse.progress &&
      celeryProgressResponse.progress.current !== undefined
        ? celeryProgressResponse.progress.current
        : 0,
    totalSteps:
      celeryProgressResponse.progress &&
      celeryProgressResponse.progress.total !== undefined
        ? celeryProgressResponse.progress.total
        : 0,
    percentComplete:
      celeryProgressResponse.progress &&
      celeryProgressResponse.progress.percent !== undefined
        ? celeryProgressResponse.progress.percent
        : 0,
    stepDescription:
      celeryProgressResponse.progress &&
      celeryProgressResponse.progress.description !== undefined
        ? celeryProgressResponse.progress.description
        : "",
    state: celeryProgressResponse.state || "",
  };
};

// /**
//  * A utility function to update comparison items with a presigned URL.
//  * This function is used to update the comparison items in the state with a presigned URL received from an API response.
//  * The function takes an object with three properties: setComparisonItems, response, and options.
//  *
//  * @param {Object} options - The options object.
//  * @param {Function} options.setComparisonItems - A state setter function for the comparison items.
//  * @param {Object} options.response - The response object from the API. It should contain two properties: image_id and presigned_url.
//  * @param {Array} options.options - An array of options. Each option is an object with two properties: index and image_id.
//  *
//  * @returns {undefined} - This function doesn't return anything; it updates the comparison items in place.
//  *
//  * @throws {console.error} - If the image_id or presigned_url properties are not provided in the response, an error will be logged to the console.
//  *
//  * @example
//  *
//  * updateComparisonItemsWithUrl({
//  *   setComparisonItems: setComparisonItems,
//  *   response: {
//  *     image_id: "front_image",
//  *     presigned_url: "https://example.com/front_image.jpg"
//  *   },
//  *   options: [
//  *     {
//  *       index: 0,
//  *       image_id: "front_image",
//  *     },
//  *     {
//  *       index: 1,
//  *       image_id: "back_image",
//  *     },
//  *   ]
//  * });
//  */
// export const updateComparisonItemsWithUrl = ({
//   setComparisonItems,
//   setProcessedComparisonItems,
//   response,
//   options,
// }) => {
//   const { image_id, presigned_url } = response;

//   if (!image_id || !presigned_url) {
//     console.error(
//       `Failed to update comparison item: image_id and presigned_url must not be null or undefined in the response`
//     );
//     return;
//   }

//   setComparisonItems((prevComparisonItems) => {
//     const updatedComparisonItems = cloneDeep(prevComparisonItems);

//     options.forEach((option) => {
//       if (option.image_id === image_id) {
//         updatedComparisonItems[option.index].fileTwo[0] = {
//           name: presigned_url,
//           size: 0,
//           path: presigned_url,
//           preview: presigned_url,
//           uniqueKey: presigned_url,
//           apiItem: true,
//         };
//       }
//     });

//     // set processedComparisonItems to the updatedComparisonItems
//     setProcessedComparisonItems(updatedComparisonItems);
//     return updatedComparisonItems;
//   });
// };

export const updateComparisonItemsPresigned = ({
  setComparisonItems,
  setProcessedComparisonItems,
  presignedUrlImages,
}) => {
  setComparisonItems((prevComparisonItems) => {
    const updatedComparisonItems = cloneDeep(prevComparisonItems);

    presignedUrlImages.forEach((presignedUrlImage) => {
      updatedComparisonItems[presignedUrlImage.index].fileTwo[0] = {
        name: presignedUrlImage.presignedUrl,
        size: 0,
        path: presignedUrlImage.presignedUrl,
        preview: presignedUrlImage.presignedUrl,
        uniqueKey: presignedUrlImage.presignedUrl,
        apiItem: true,
      };
    });

    // set processedComparisonItems to the updatedComparisonItems
    setProcessedComparisonItems(updatedComparisonItems);
    return updatedComparisonItems;
  });
};

/**
 * A function that clears a specific image from the comparison items.
 * This function updates the comparison items state by clearing the specified image.
 *
 * @param {Object} params - The parameters for the function.
 * @param {function} params.setComparisonItems - The state setter function for the comparison items.
 * @param {number} params.imageIndex - The index of the image in the comparison items array.
 * @param {string} params.imageKey - The key of the image in the comparison item object.
 *
 * @returns {undefined} - This function does not return a value. It only causes a side effect of updating the state of comparison items.
 *
 * @example
 *
 * clearComparisonItemsImage({
 *   setComparisonItems: setComparisonItems,
 *   imageIndex: 0,
 *   imageKey: 'fileOne',
 * });
 */
export const clearComparisonItemsImage = ({
  setComparisonItems,
  imageIndex,
  imageKey,
}) => {
  setComparisonItems((prevComparisonItems) => {
    const updatedComparisonItems = cloneDeep(prevComparisonItems);
    updatedComparisonItems[imageIndex][imageKey] = [];
    return updatedComparisonItems;
  });
};

// processSettingImagePairs START
// These functions processes and set image pairs into state from sanitized API response data.
// It is ensured that valid images and their details are set, and celery task completion status
// is respected for image setting.

/**
 * Helper function to create a comparison item image
 * Creates a comparison image object from provided image name and details.
 *
 * @function
 * @param {string} imageName - Name of the image.
 * @param {Object} imageDetails - Details of the image which includes name and size.
 * @returns {Object} An object representing an image to be used in a comparison item.
 */
const createComparisonItemImage = (imageName, imageDetails) => ({
  name: imageDetails?.name || "",
  size: imageDetails?.size || 0,
  path: `${getImageBaseUrl()}${imageName}`,
  preview: `${getImageBaseUrl()}${imageName}`,
  uniqueKey: imageName,
  apiItem: true,
});

/**
 * Helper function to create image details
 * Creates an image details object from provided image details.
 *
 * @function
 * @param {Object} imageDetails - Original details of the image which includes name, size, and caption.
 * @returns {Object} A new object with the name, size, and caption fields. If no imageDetails object or certain fields are provided, it sets default values.
 */
const createImageDetails = (imageDetails = {}) => ({
  name: imageDetails?.name || "",
  size: imageDetails?.size || 0,
  caption: imageDetails?.caption || "",
});

/**
 * Helper function to get image and its details
 * Fetches an image and its details from the sanitized response data.
 *
 * @function
 * @param {Object} sanitizedEntryDataResponse - Sanitized API response data.
 * @param {string} imageName - Name of the image.
 * @param {string} imageDetailsName - Name of the image details field in the response.
 * @returns {Object} An object containing the image and its details.
 */
const getImageAndDetails = (
  sanitizedEntryDataResponse,
  imageName,
  imageDetailsName
) => {
  const image = sanitizedEntryDataResponse[imageName];
  const imageDetails = sanitizedEntryDataResponse[imageDetailsName];

  return {
    image: image ? createComparisonItemImage(image, imageDetails) : undefined,
    imageDetails: createImageDetails(imageDetails),
  };
};

/**
 * The main function that processes image pairs
 * Processes and sets images from the sanitized API response data into state.
 *
 * @function
 * @param {Object} options - The options object.
 * @param {Object} options.sanitizedEntryDataResponse - Sanitized API response data.
 * @param {boolean} [options.celeryTaskCompleted=true] - A flag indicating whether the celery task is completed.
 * @param {Array} options.imagePairs - An array of image pair objects.
 * @returns {Object} An object containing the processed comparison items.
 */
export const processSettingImagePairs = ({
  sanitizedEntryDataResponse,
  celeryTaskCompleted = true,
  imagePairs,
  fillEmptyComparisonItem = true,
}) => {
  const processedComparisonItems = [];

  for (const [i, { image_one_name, image_two_name }] of imagePairs.entries()) {
    const imageOneName = image_one_name;
    const imageTwoName = image_two_name;

    const { image: imageOne, imageDetails: imageOneDetails } =
      getImageAndDetails(
        sanitizedEntryDataResponse,
        imageOneName,
        `image_one_${i}_details`
      );

    const { image: imageTwo, imageDetails: imageTwoDetails } =
      getImageAndDetails(
        sanitizedEntryDataResponse,
        imageTwoName,
        `image_two_${i}_details`
      );

    const pairConfiguration =
      sanitizedEntryDataResponse[`pair_configuration_${i}`];

    if (
      imageOne &&
      imageOneDetails &&
      pairConfiguration &&
      (imageTwo || !celeryTaskCompleted) &&
      (imageTwoDetails || !celeryTaskCompleted)
    ) {
      const comparisonItem = {
        fileOne: [imageOne],
        fileTwo: celeryTaskCompleted ? [imageTwo] : [],
        fileOneImage: [],
        fileTwoImage: [],
        fileOneDetails: imageOneDetails,
        fileTwoDetails: celeryTaskCompleted
          ? imageTwoDetails
          : createImageDetails(),
        ...pairConfiguration,
      };

      processedComparisonItems.push(comparisonItem);
    } else {
      if (fillEmptyComparisonItem) {
        processedComparisonItems.push(baseComparisonItem);
      }
    }
  }

  return { processedComparisonItems };
};
// processSettingImagePairs END

/**
 * This function sanitizes the raw response from the API for a single entry. It checks each field necessary for the
 * application to function correctly, and if a field is missing, it sets a default value.
 *
 * @function sanitizeEntryDataResponseBase
 * @param {Object} params - A parameter object
 * @param {Object} params.entryDataResponse - The raw API response data for a single entry.
 * @param {Object} params.defaultValues - An object containing the default values for each field.
 * @returns {Object} The sanitized API response data.
 */
export const sanitizeEntryDataResponseBase = ({
  entryDataResponse,
  defaultValues,
}) => {
  let sanitizedResponse = {};

  for (let key in defaultValues) {
    if (
      entryDataResponse.hasOwnProperty(key) &&
      entryDataResponse[key] !== undefined &&
      entryDataResponse[key] !== null
    ) {
      sanitizedResponse[key] = entryDataResponse[key];
    } else {
      sanitizedResponse[key] = defaultValues[key];
    }
  }

  return sanitizedResponse;
};

// Function to analyze the Celery status and task completion
export const analyzeCeleryStatus = ({
  sanitizedEntryDataResponse,
  setCeleryID,
}) => {
  const completedCeleryTaskStatus =
    sanitizedEntryDataResponse.completed_celery_task_success;
  const currentCeleryTaskId = sanitizedEntryDataResponse.celery_task_id;
  const completedCeleryTaskId =
    sanitizedEntryDataResponse.completed_celery_task_id;

  setCeleryID(currentCeleryTaskId);

  let celeryStatusCode = "new_entry";
  if (
    currentCeleryTaskId === "" &&
    completedCeleryTaskId === "" &&
    !completedCeleryTaskStatus
  ) {
    return celeryStatusCode;
  } else if (
    completedCeleryTaskStatus &&
    currentCeleryTaskId === completedCeleryTaskId
  ) {
    celeryStatusCode = "completed";
  } else {
    celeryStatusCode = "in_progress";
  }
  return celeryStatusCode;
};
